import { useEffect, useRef, useCallback } from 'react';
import LeftSidebar from './LeftSidebar';
import ArticleRightSidebar from './ArticleRightSidebar';

const PageLayout = ({ children, isArticle = false }) => {
  const layoutRef = useRef(null);
  const leftRef  = useRef(null);
  const rightRef = useRef(null);
  const rafRef   = useRef(null);

  const checkSidebars = useCallback(() => {
    const layout = layoutRef.current;
    const left   = leftRef.current;
    const right  = rightRef.current;
    if (!layout) return;

    // Skip on mobile/tablet — CSS handles layout there
    if (window.innerWidth < 992) return;

    const scrollY      = window.scrollY;
    const vh           = window.innerHeight;
    const docLayoutTop = layout.getBoundingClientRect().top + scrollY;

    // Each sidebar collapses independently based on its own natural content height.
    // scrollHeight gives the full content height regardless of overflow:clip.
    // Guard > 80 prevents premature collapse before widget data renders.
    const leftH  = left  ? left.scrollHeight  : 0;
    const rightH = right ? right.scrollHeight : 0;

    const leftDone  = leftH  > 80 && scrollY + vh > docLayoutTop + leftH  + 40;
    const rightDone = rightH > 80 && scrollY + vh > docLayoutTop + rightH + 40;

    layout.classList.toggle('layout-left-done',  leftDone);
    layout.classList.toggle('layout-right-done', rightDone);
  }, []);

  const onScroll = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(checkSidebars);
  }, [checkSidebars]);

  useEffect(() => {
    // Defer initial measurement until widget content has had a chance to render.
    // Running immediately on mount reads near-zero heights and prematurely collapses.
    const timer = setTimeout(checkSidebars, 400);
    window.addEventListener('scroll', onScroll,      { passive: true });
    window.addEventListener('resize', checkSidebars, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', checkSidebars);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onScroll, checkSidebars]);

  // Re-evaluate when any column changes height — each sidebar drives its own threshold.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(checkSidebars);
    if (layoutRef.current) ro.observe(layoutRef.current);
    if (leftRef.current)   ro.observe(leftRef.current);
    if (rightRef.current)  ro.observe(rightRef.current);
    return () => ro.disconnect();
  }, [checkSidebars]);

  return (
    <div className={`page-layout${isArticle ? ' page-layout--article' : ''}`} ref={layoutRef}>
      <div className="page-layout__left" ref={leftRef}>
        <div className="page-layout__left-inner">
          <LeftSidebar />
        </div>
      </div>
      <div className="page-layout__main">
        {children}
      </div>
      <div className="page-layout__right" ref={rightRef}>
        <div className="page-layout__right-inner">
          <ArticleRightSidebar />
        </div>
      </div>
    </div>
  );
};

export default PageLayout;
